import React from "react";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  HeartIcon,
  MapPinIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";
import aboutImg from "../assests/images/aboutUs.jpg";
import CTASection from "../components/CTASection";

import { Helmet } from "react-helmet-async";

export default function AboutUs() {
  const features = [
    {
      icon: UsersIcon,
      number: "01",
      title: "Trusted by Travelers",
      desc: "Thousands of travelers rely on us to discover comfortable stays and great hotel deals.",
    },
    {
      icon: MapPinIcon,
      number: "02",
      title: "Prime Locations",
      desc: "Discover hotels close to Dubai's beaches, attractions, shopping and business districts.",
    },
    {
      icon: HeartIcon,
      number: "03",
      title: "Guest First",
      desc: "We focus on making every part of your hotel search and booking experience simple.",
    },
    {
      icon: ShieldCheckIcon,
      number: "04",
      title: "Secure & Reliable",
      desc: "Book with confidence through a platform designed around trust and convenience.",
    },
  ];

  const stats = [
    {
      value: "500+",
      label: "Hotels",
    },
    {
      value: "10K+",
      label: "Travelers",
    },
    {
      value: "24/7",
      label: "Support",
    },
    {
      value: "25+",
      label: "Years Experience",
    },
  ];

  return (
    <>
      {/* =========================================================
                SEO
            ========================================================= */}
      <Helmet>
        <title>About Us | Beach Hotels Dubai</title>

        <meta
          name="description"
          content="Learn more about Beach Hotels Dubai, your trusted platform for affordable hotel bookings and the best budget stays in Dubai."
        />

        <link
          rel="canonical"
          href="https://dubaihotelrates.com/about-us"
        />

        <meta
          property="og:title"
          content="About Us | Beach Hotels Dubai"
        />

        <meta
          property="og:description"
          content="Learn more about Beach Hotels Dubai, your trusted platform for affordable hotel bookings and the best budget stays in Dubai."
        />

        <meta
          property="og:url"
          content="https://dubaihotelrates.com/about-us"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="About Us | Beach Hotels Dubai"
        />

        <meta
          name="twitter:description"
          content="Learn more about Beach Hotels Dubai, your trusted platform for affordable hotel bookings and the best budget stays in Dubai."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <div className="w-full overflow-hidden bg-white">

        {/* =====================================================
                    HERO
                ====================================================== */}

        <Hero
          variant="inner"
          title="About Beach Hotels Dubai"
          subtitle="Helping travelers discover better stays, better value, and unforgettable experiences in Dubai."
          backgroundImage={heroImg}
        />


        {/* =====================================================
                    INTRO / STORY
                ====================================================== */}

        <section className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-28">

          {/* Decorative */}
          <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 rounded-full bg-[#174EA6]/5 blur-3xl" />

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">

              {/* =================================================
                                IMAGE AREA
                            ================================================= */}

              <div className="relative">

                <div className="relative ml-0 max-w-xl lg:ml-6">

                  {/* Image */}
                  <div className="relative overflow-hidden rounded-[32px]">

                    <img
                      src={aboutImg}
                      alt="Beach Hotels Dubai"
                      className="
                                                h-[380px]
                                                w-full
                                                object-cover
                                                transition-transform
                                                duration-700
                                                hover:scale-105
                                                sm:h-[480px]
                                                lg:h-[570px]
                                            "
                    />

                    <div className="
                                            absolute inset-0
                                            bg-gradient-to-t
                                            from-[#002B45]/60
                                            via-transparent
                                            to-transparent
                                        " />
                  </div>

                  {/* Floating experience card */}
                  <div className="
                                        absolute
                                        -bottom-7
                                        left-5
                                        rounded-[22px]
                                        bg-white
                                        px-6
                                        py-5
                                        shadow-[0_20px_50px_rgba(15,23,42,0.18)]
                                        sm:left-8
                                        sm:px-7
                                        sm:py-6
                                    ">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                      Experience
                    </p>

                    <div className="mt-1 flex items-end gap-2">
                      <span className="text-3xl font-extrabold text-[#174EA6] sm:text-4xl">
                        25+
                      </span>

                      <span className="mb-1 text-sm font-medium text-slate-500">
                        Years
                      </span>
                    </div>
                  </div>

                  {/* Gold corner */}
                  <div className="
                                        absolute
                                        -left-5
                                        -top-5
                                        h-20
                                        w-20
                                        rounded-tl-[28px]
                                        border-l-4
                                        border-t-4
                                        border-[#FFC928]
                                    " />

                  {/* Small floating badge */}
                  <div className="
                                        absolute
                                        right-4
                                        top-4
                                        flex
                                        items-center
                                        gap-2
                                        rounded-full
                                        border
                                        border-white/20
                                        bg-[#002B45]/75
                                        px-4
                                        py-2
                                        backdrop-blur-md
                                    ">
                    <SparklesIcon className="h-4 w-4 text-[#FFC928]" />

                    <span className="text-xs font-bold text-white">
                      Dubai Stays
                    </span>
                  </div>
                </div>
              </div>


              {/* =================================================
                                CONTENT
                            ================================================= */}

              <div>

                <div className="mb-5 flex items-center gap-3">

                  <span className="h-[2px] w-10 bg-[#FFC928]" />

                  <span className="
                                        text-xs
                                        font-bold
                                        uppercase
                                        tracking-[0.2em]
                                        text-[#174EA6]
                                    ">
                    Who We Are
                  </span>
                </div>

                <h2 className="
                                    text-3xl
                                    font-extrabold
                                    leading-tight
                                    tracking-tight
                                    text-[#0F172A]
                                    sm:text-4xl
                                    lg:text-5xl
                                ">
                  Making Every Stay

                  <span className="block text-[#174EA6]">
                    Worth Remembering
                  </span>
                </h2>

                <p className="
                                    mt-6
                                    text-sm
                                    leading-7
                                    text-slate-600
                                    sm:text-base
                                ">
                  We started with a simple mission: make
                  finding the right hotel easy, fast, and
                  reliable. Whether you're visiting Dubai
                  for business, relaxation, shopping, or a
                  memorable getaway, we help you discover
                  accommodation that fits your needs.
                </p>

                <p className="
                                    mt-4
                                    text-sm
                                    leading-7
                                    text-slate-600
                                    sm:text-base
                                ">
                  Through our growing network of hotels and
                  travel partners, we bring together
                  comfortable stays, competitive rates, and
                  a seamless booking experience.
                </p>


                {/* Highlights */}
                <div className="mt-8 space-y-4">

                  <div className="flex items-start gap-4">

                    <div className="
                                            flex
                                            h-10
                                            w-10
                                            shrink-0
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-[#EAF3FF]
                                        ">
                      <CheckCircleIcon className="h-5 w-5 text-[#174EA6]" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-[#0F172A]">
                        Carefully Selected Stays
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                        Discover accommodation selected
                        around comfort, location and value.
                      </p>
                    </div>
                  </div>


                  <div className="flex items-start gap-4">

                    <div className="
                                            flex
                                            h-10
                                            w-10
                                            shrink-0
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-[#FFF7D6]
                                        ">
                      <CheckCircleIcon className="h-5 w-5 text-[#9A7418]" />
                    </div>

                    <div>
                      <h3 className="text-sm font-bold text-[#0F172A]">
                        Value Focused
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                        Find competitive hotel rates without
                        compromising on your stay.
                      </p>
                    </div>
                  </div>

                </div>


                {/* Stats */}
                <div className="
                                    mt-10
                                    grid
                                    grid-cols-2
                                    gap-3
                                    sm:grid-cols-4
                                ">

                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      className="
                                                rounded-2xl
                                                border
                                                border-slate-100
                                                bg-[#F7FAFF]
                                                px-4
                                                py-4
                                            "
                    >
                      <div className="text-xl font-extrabold text-[#174EA6] sm:text-2xl">
                        {stat.value}
                      </div>

                      <div className="mt-1 text-[10px] font-medium text-slate-500 sm:text-xs">
                        {stat.label}
                      </div>
                    </div>
                  ))}

                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
                    MISSION / VISION
                ====================================================== */}

        <section className="
                    relative
                    overflow-hidden
                    bg-[#F3F8FF]
                    py-16
                    sm:py-20
                    lg:py-24
                ">

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

            {/* Section heading */}
            <div className="mx-auto mb-12 max-w-2xl text-center">

              <div className="mb-4 flex items-center justify-center gap-3">

                <span className="h-[2px] w-8 bg-[#FFC928]" />

                <span className="
                                    text-xs
                                    font-bold
                                    uppercase
                                    tracking-[0.2em]
                                    text-[#174EA6]
                                ">
                  Our Direction
                </span>

                <span className="h-[2px] w-8 bg-[#FFC928]" />

              </div>

              <h2 className="
                                text-3xl
                                font-extrabold
                                text-[#0F172A]
                                sm:text-4xl
                            ">
                Built Around Better Travel
              </h2>

              <p className="
                                mt-4
                                text-sm
                                leading-7
                                text-slate-500
                                sm:text-base
                            ">
                Everything we do starts with one goal:
                creating a simpler and more enjoyable way
                to discover Dubai.
              </p>

            </div>


            {/* Mission / Vision */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

              {/* Mission */}
              <div className="
                                group
                                relative
                                min-h-[340px]
                                overflow-hidden
                                rounded-[30px]
                                bg-[#002B45]
                                p-7
                                shadow-[0_20px_60px_rgba(0,43,69,0.15)]
                                sm:p-10
                            ">

                <div className="
                                    pointer-events-none
                                    absolute
                                    -right-24
                                    -top-24
                                    h-72
                                    w-72
                                    rounded-full
                                    bg-[#174EA6]/40
                                    blur-3xl
                                " />

                <div className="
                                    pointer-events-none
                                    absolute
                                    -bottom-32
                                    -left-20
                                    h-64
                                    w-64
                                    rounded-full
                                    bg-[#FFC928]/10
                                    blur-3xl
                                " />

                <div className="relative flex h-full flex-col justify-between">

                  <div>

                    <div className="
                                            flex
                                            h-14
                                            w-14
                                            items-center
                                            justify-center
                                            rounded-2xl
                                            bg-[#FFC928]
                                            text-[#002B45]
                                        ">
                      <SparklesIcon className="h-7 w-7" />
                    </div>

                    <div className="mt-8">

                      <p className="
                                                text-[10px]
                                                font-bold
                                                uppercase
                                                tracking-[0.2em]
                                                text-[#FFC928]
                                            ">
                        Our Mission
                      </p>

                      <h3 className="
                                                mt-3
                                                text-2xl
                                                font-extrabold
                                                text-white
                                                sm:text-3xl
                                            ">
                        Simplifying the way you travel.
                      </h3>

                      <p className="
                                                mt-4
                                                max-w-lg
                                                text-sm
                                                leading-7
                                                text-slate-300
                                            ">
                        To simplify travel by connecting
                        people with great hotels,
                        competitive prices, and a booking
                        experience they can trust.
                      </p>

                    </div>

                  </div>

                  <div className="mt-8 flex items-center gap-2 text-xs font-bold text-white/50">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#FFC928]" />
                    Making travel simpler
                  </div>

                </div>

              </div>


              {/* Vision */}
              <div className="
                                group
                                relative
                                min-h-[340px]
                                overflow-hidden
                                rounded-[30px]
                                border
                                border-slate-200
                                bg-white
                                p-7
                                shadow-sm
                                sm:p-10
                            ">

                <div className="
                                    pointer-events-none
                                    absolute
                                    -right-24
                                    -top-24
                                    h-72
                                    w-72
                                    rounded-full
                                    bg-[#174EA6]/5
                                    blur-3xl
                                " />

                <div className="relative flex h-full flex-col justify-between">

                  <div>

                    <div className="
                                            flex
                                            h-14
                                            w-14
                                            items-center
                                            justify-center
                                            rounded-2xl
                                            bg-[#EAF3FF]
                                            text-[#174EA6]
                                        ">
                      <GlobeAltIcon className="h-7 w-7" />
                    </div>

                    <div className="mt-8">

                      <p className="
                                                text-[10px]
                                                font-bold
                                                uppercase
                                                tracking-[0.2em]
                                                text-[#174EA6]
                                            ">
                        Our Vision
                      </p>

                      <h3 className="
                                                mt-3
                                                text-2xl
                                                font-extrabold
                                                text-[#0F172A]
                                                sm:text-3xl
                                            ">
                        A better way to discover Dubai.
                      </h3>

                      <p className="
                                                mt-4
                                                max-w-lg
                                                text-sm
                                                leading-7
                                                text-slate-500
                                            ">
                        To become a trusted platform for
                        hotel booking and travel
                        experiences, helping travelers
                        find memorable stays wherever
                        their journey takes them.
                      </p>

                    </div>

                  </div>

                  <div className="mt-8 flex items-center gap-2 text-xs font-bold text-slate-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#174EA6]" />
                    Discover more with confidence
                  </div>

                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
                    WHY CHOOSE US
                ====================================================== */}

        <section className="
                    relative
                    overflow-hidden
                    bg-white
                    py-16
                    sm:py-20
                    lg:py-28
                ">

          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

            {/* Heading */}
            <div className="
                            mb-12
                            flex
                            flex-col
                            gap-5
                            lg:flex-row
                            lg:items-end
                            lg:justify-between
                        ">

              <div className="max-w-2xl">

                <div className="mb-4 flex items-center gap-3">

                  <span className="h-[2px] w-10 bg-[#FFC928]" />

                  <span className="
                                        text-xs
                                        font-bold
                                        uppercase
                                        tracking-[0.2em]
                                        text-[#174EA6]
                                    ">
                    Why Beach Hotels Dubai
                  </span>

                </div>

                <h2 className="
                                    text-3xl
                                    font-extrabold
                                    tracking-tight
                                    text-[#0F172A]
                                    sm:text-4xl
                                    lg:text-5xl
                                ">
                  Travel With
                  <span className="text-[#174EA6]">
                    {" "}Confidence
                  </span>
                </h2>

              </div>

              <p className="
                                max-w-md
                                text-sm
                                leading-7
                                text-slate-500
                                lg:text-right
                            ">
                Everything we do is focused on making your
                hotel search easier, more transparent,
                and more enjoyable.
              </p>

            </div>


            {/* Feature cards */}
            <div className="
                            grid
                            grid-cols-1
                            gap-4
                            sm:grid-cols-2
                            lg:grid-cols-4
                        ">

              {features.map((item, index) => {

                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="
                                            group
                                            relative
                                            overflow-hidden
                                            rounded-[26px]
                                            border
                                            border-slate-200
                                            bg-white
                                            p-7
                                            transition-all
                                            duration-500
                                            hover:-translate-y-2
                                            hover:border-[#174EA6]/20
                                            hover:shadow-[0_20px_50px_rgba(23,78,166,0.10)]
                                        "
                  >

                    {/* Number */}
                    <span className="
                                            absolute
                                            right-6
                                            top-5
                                            text-xs
                                            font-extrabold
                                            text-slate-100
                                            transition-colors
                                            duration-300
                                            group-hover:text-[#EAF3FF]
                                        ">
                      {item.number}
                    </span>


                    {/* Icon */}
                    <div className="
                                            flex
                                            h-14
                                            w-14
                                            items-center
                                            justify-center
                                            rounded-2xl
                                            bg-[#EAF3FF]
                                            text-[#174EA6]
                                            transition-all
                                            duration-300
                                            group-hover:bg-[#174EA6]
                                            group-hover:text-white
                                        ">
                      <Icon className="h-7 w-7" />
                    </div>


                    <h3 className="
                                            mt-6
                                            text-lg
                                            font-bold
                                            text-[#0F172A]
                                        ">
                      {item.title}
                    </h3>

                    <p className="
                                            mt-3
                                            text-sm
                                            leading-6
                                            text-slate-500
                                        ">
                      {item.desc}
                    </p>


                    <div className="
                                            mt-6
                                            flex
                                            items-center
                                            gap-2
                                            text-xs
                                            font-bold
                                            text-[#174EA6]
                                        ">
                      <span className="
                                                h-1.5
                                                w-1.5
                                                rounded-full
                                                bg-[#FFC928]
                                            " />

                      Travel with confidence
                    </div>


                    {/* Hover line */}
                    <div className="
                                            absolute
                                            bottom-0
                                            left-0
                                            h-1
                                            w-0
                                            bg-gradient-to-r
                                            from-[#174EA6]
                                            to-[#FFC928]
                                            transition-all
                                            duration-500
                                            group-hover:w-full
                                        " />

                  </div>
                );
              })}

            </div>

          </div>
        </section>





        {/* =====================================================
                    FINAL CTA
                ====================================================== */}

        <CTASection />

      </div>
    </>
  );
}